import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseLink from '../../components/misc/BaseLink.jsx';
import { useLazyGetDashboardQuery } from '../../api/contact.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetCountriesQuery } from '../../api/misc.js';
import BaseCard from '../../components/misc/BaseCard.jsx';
import DashboardCompanyCarousel from './DashboardCompanyCarousel.jsx';
import DashboardCountryCarousel from '../../components/misc/DashboardCountryCarousel.jsx';
import DashboardMetricsCarousel from '../../components/misc/DashboardMetricsCarousel.jsx';
import circle from '../../resources/images/circle.png';
import BaseButton from '../../components/form/BaseButton.jsx';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';

const ContactsDashboard = () => {
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user } = useCurrentUser();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState([]);
    const [dashboardResponse] = useLazyGetDashboardQuery();
    const [isLoading, setIsLoading] = useState(false);

    const requestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id] ? organizationSwitch[user?.id].private : null;
        let organizations =
            organizationSwitch[user?.id] && organizationSwitch[user?.id].organizations
                ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                      (key) => organizationSwitch[user?.id].organizations[key] === true
                  )
                : [];
        return {
            ...(organizations.length > 0 ? { organizations: organizations } : {}),
            private: privateOrg
        };
    }, [organizationSwitch, user?.id]);

    const applyFilters = useCallback(async () => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            setIsLoading(true);
            const result = await dashboardResponse(requestParams);
            if (result?.data?.response) {
                setDashboardData(result.data.response);
            }
            setIsLoading(false);
        }
    }, [dashboardResponse, requestParams]);

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({
                label: country.name,
                value: country.id,
                code: country.code
            }));
        }

        return [];
    }, [countriesData]);

    useEffect(() => {
        setDashboardData([]);
        applyFilters().then();
    }, [applyFilters]);

    return (
        <AuthLayout title='Contacts Dashboard' backRoute='/dashboard'>
            <>
                {isLoading && <ThreeDotsLoader className='mt-10' />}
                {dashboardData && !isLoading && (
                    <>
                        <div className='flex max-w-3xl w-full mx-auto mt-4 px-8'>
                            <div className='grid sm:grid-cols-2 sm:gap-4 w-full'>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_contacts))}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Contacts</div>
                                </BaseCard>
                                <BaseCard className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center my-0 sm:my-6'>
                                    <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                                        {formatNumbers(Number(dashboardData.total_companies))}
                                    </div>
                                    <div className='font-bold text-theme1-title2 mt-1'>Companies</div>
                                </BaseCard>
                            </div>
                        </div>
                        <div className='flex max-w-3xl w-full mx-auto px-8 mt-6 sm:mt-0'>
                            <BaseLink className='btn-link-primary' to='/contacts/list'>
                                Contacts List
                            </BaseLink>
                        </div>
                        {dashboardData.total_contacts > 0 ? (
                            <>
                                {dashboardData.contacts_per_affiliation?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-10 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Political Affiliation
                                            </div>
                                            <DashboardMetricsCarousel
                                                items={dashboardData.contacts_per_affiliation || []}
                                                className='mb-6'
                                                total={dashboardData.total_contacts}
                                                filterKey='political_affiliations'
                                                clickAction='filter'
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.skills_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>Skills</div>
                                            <DashboardMetricsCarousel
                                                items={dashboardData.skills_distribution || []}
                                                className='mb-6'
                                                total={dashboardData.total_contacts}
                                                filterKey='skills'
                                                clickAction='filter'
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.job_titles_distribution?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>Job Titles</div>
                                            <DashboardMetricsCarousel
                                                items={dashboardData.job_titles_distribution || []}
                                                className='mb-6'
                                                total={dashboardData.total_contacts}
                                                clickAction='search'
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.contacts_per_company?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Contacts by Company
                                            </div>
                                            <DashboardCompanyCarousel
                                                items={dashboardData.contacts_per_company || []}
                                                className='mb-6'
                                            />
                                        </div>
                                    </>
                                )}
                                {dashboardData.contacts_per_country?.length > 0 && (
                                    <>
                                        <div className='max-w-3xl w-full mx-auto mt-8 px-8'>
                                            <div className='text-theme1-title2 font-bold text-[22px]'>
                                                Contacts by Country
                                            </div>
                                            <DashboardCountryCarousel
                                                items={dashboardData.contacts_per_country || []}
                                                className='mb-6'
                                                countries={countries}
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div className='py-12 flex flex-col justify-center items-center'>
                                    <div className='text-2xl text-theme1-cardStroke font-light max-w-80 text-center'>
                                        You don't have any contacts yet
                                    </div>
                                    <img alt='No contacts' src={circle} className='my-8 w-40 h-40' />
                                    <BaseButton
                                        className='btn-red-outline max-w-96'
                                        onClick={() => navigate(`/contacts/add`)}
                                        textValue='Add new contact'
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </>
        </AuthLayout>
    );
};

export default ContactsDashboard;
